/**
 * Signal log — the instrument panel beside the Plan of Record (SIG-2).
 *
 * The moment it catches (02B §5): "What's on the instruments — and what
 * have we not answered?"
 *
 * Shows the watch band + the unified signal log (human and machine
 * signals in one list). Actions are the canon 23 §4.1 present-tense set:
 * confirm, classify, dismiss-with-rationale, record a new signal. No
 * condition letter appears anywhere; overdue counts come from the
 * signal_events_unanswered view — the one truth source for that predicate.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Plus, Radio, ShieldCheck, Sparkles } from 'lucide-react';
import {
  proposeSignalRouting,
  type SignalRoutingProposal,
} from '../../lib/llmSignalRoutingAdvisorService';
import {
  SIGNAL_ROUTING_CLASSES,
  getSignalOccurrence,
  type SignalEventRow,
  type SignalRoutingClass,
} from '../../types/signal-events';
import {
  classifySignalEvent,
  confirmSignalEvent,
  dismissSignalEvent,
  extractExpectations,
  listSignalEvents,
  listSignalResponseRecord,
  listUnansweredSignals,
  recordSignalVerdict,
  type SignalResponseRecordEntry,
  type SignalVerdict,
} from '../../lib/signalEventsService';
import { reopenSignalEvent } from '../../lib/reversals/reversalsService';
import { GhostButton, PorModal, PrimaryButton } from '../plan-of-record/porUi';
import { AnswerSignalModal } from './AnswerSignalModal';
import { SignalCaptureModal } from './SignalCaptureModal';
import { SignalResponseRecord } from './SignalResponseRecord';
import { SIGNAL_WATCH_COPY, deriveSignalWatch } from './signalWatch';
import { formatCalendarDate } from '../../lib/calendarDate';

const VERDICT_LABELS: Record<SignalVerdict, string> = {
  held: 'Held',
  broke: 'Broke',
  unobservable: 'Unobservable',
};

const ROUTING_SHORT: Record<SignalRoutingClass, string> = {
  feasibility_model: 'feasibility',
  value_model: 'value',
  assumption_base: 'assumption',
  mandate_frame: 'mandate',
  record_error: 'record error',
};

const WATCH_ACCENT = {
  clear: 'border-primary-200 bg-primary-50 text-primary-800',
  watching: 'border-electric-mustard-200 bg-electric-mustard-50 text-electric-mustard-800',
  overdue: 'border-luminous-coral-200 bg-luminous-coral-50 text-luminous-coral-800',
} as const;

function occurrenceLabel(row: SignalEventRow): string {
  return getSignalOccurrence(row.occurrence)?.label ?? row.occurrence;
}

function age(iso: string | null): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export function SignalLog({
  workspaceId,
  porId,
  cycleId,
  canAct,
  itemOptions,
  replanCycleId,
  planCycleId,
}: {
  workspaceId: string;
  porId?: string;
  cycleId?: string;
  canAct: boolean;
  /** Plan commitments an answer can attach to (threaded to the Answer modal). */
  itemOptions?: { id: string; title: string }[];
  /**
   * The plan cycle a change-course answer replans (threaded to the Answer
   * modal). Distinct from `cycleId`, which FILTERS the log — the plan
   * overview shows workspace-wide signals but still knows which plan a
   * change-course answer should carry into.
   */
  replanCycleId?: string | null;
  /** Planning-family scope (682): new captures attach to this plan cycle. */
  planCycleId?: string;
}) {
  const [signals, setSignals] = useState<SignalEventRow[]>([]);
  const [unansweredIds, setUnansweredIds] = useState<Set<string>>(new Set());
  const [responses, setResponses] = useState<SignalResponseRecordEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [answering, setAnswering] = useState<{ row: SignalEventRow; kind: 'hold' | 'change' } | null>(null);
  const [dismissing, setDismissing] = useState<SignalEventRow | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [recordingVerdict, setRecordingVerdict] = useState<SignalEventRow | null>(null);
  const [reopening, setReopening] = useState<SignalEventRow | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [rows, unanswered, record] = await Promise.all([
        listSignalEvents({ workspaceId, porId, cycleId }),
        listUnansweredSignals(workspaceId, { porId, cycleId }),
        listSignalResponseRecord({ workspaceId, porId }),
      ]);
      setSignals(rows);
      setUnansweredIds(new Set(unanswered.map((u) => u.id).filter((id): id is string => !!id)));
      setResponses(record);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load signals.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, porId, cycleId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const open = signals.filter((s) => s.status !== 'dismissed');
  const dismissed = signals.filter((s) => s.status === 'dismissed');
  const watch = deriveSignalWatch(open, unansweredIds.size);
  const copy = SIGNAL_WATCH_COPY[watch.state];

  // Latest change/hold answer per signal — rendered inline on its row as
  // "On the record" (entries arrive newest-first, so first per id wins).
  const answeredBySignal = new Map<string, SignalResponseRecordEntry>();
  for (const r of responses) {
    if ((r.responseKind === 'change' || r.responseKind === 'hold') && !answeredBySignal.has(r.signalEventId)) {
      answeredBySignal.set(r.signalEventId, r);
    }
  }
  const inlineAnsweredIds = open.filter((s) => answeredBySignal.has(s.id)).map((s) => s.id);

  return (
    <section>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          The watch
          <span className="ml-2 normal-case tracking-normal font-normal text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            every signal ages toward an answer
          </span>
        </h3>
        {canAct && (
          <button
            onClick={() => setCapturing(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
          >
            <Plus className="h-3.5 w-3.5" /> Record a signal
          </button>
        )}
      </div>

      {/* Watch band — the present state; never a letter */}
      <div className={`flex items-start gap-3  border px-4 py-3 ${WATCH_ACCENT[watch.state]}`}>
        {watch.state === 'overdue' ? (
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        ) : watch.state === 'watching' ? (
          <Radio className="h-5 w-5 flex-shrink-0 mt-0.5" />
        ) : (
          <ShieldCheck className="h-5 w-5 flex-shrink-0 mt-0.5" />
        )}
        <div>
          <p className="text-sm font-semibold">
            {copy.label}
            {watch.state === 'overdue' && ` — ${watch.unansweredCount} overdue`}
          </p>
          <p className="text-xs mt-0.5 opacity-90">{copy.blurb}</p>
        </div>
      </div>

      {loadError && <p className="text-xs text-luminous-coral-700 mt-2">{loadError}</p>}
      {loading && <p className="text-xs text-[var(--text-ter)] mt-2 dark:text-[var(--text-ter)]">Loading signals…</p>}

      {!loading && open.length > 0 && (
        <ul className="mt-3 divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border border-[var(--rule)] dark:border-[var(--rule)]">
          {open.map((row) => (
            <SignalRowItem
              key={row.id}
              row={row}
              overdue={unansweredIds.has(row.id)}
              canAct={canAct}
              answered={answeredBySignal.get(row.id)}
              onConfirm={async () => {
                const r = await confirmSignalEvent(row.id);
                await refresh();
                return r;
              }}
              onClassify={async (rc, rationale) => {
                const r = await classifySignalEvent(row.id, rc, rationale);
                await refresh();
                return r;
              }}
              onAnswer={(kind) => setAnswering({ row, kind })}
              onDismiss={() => setDismissing(row)}
              onRecordVerdict={() => setRecordingVerdict(row)}
              onReopen={() => setReopening(row)}
            />
          ))}
        </ul>
      )}

      {!loading && dismissed.length > 0 && (
        <button
          onClick={() => setShowDismissed((v) => !v)}
          className="mt-2 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)]"
        >
          {showDismissed ? 'Hide' : 'Show'} {dismissed.length} dismissed
        </button>
      )}
      {!loading && showDismissed && dismissed.length > 0 && (
        <ul className="mt-2 space-y-1">
          {dismissed.map((row) => (
            <li
              key={row.id}
              className="flex items-center justify-between gap-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]"
            >
              <span>
                <span className="line-through">{occurrenceLabel(row)}</span>
                {row.dismissed_rationale && <> — {row.dismissed_rationale}</>}
              </span>
              {canAct && (
                <button
                  onClick={() => setReopening(row)}
                  className="flex-shrink-0 text-[var(--text-ter)] hover:underline"
                >
                  Reopen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <SignalResponseRecord
        workspaceId={workspaceId}
        porId={porId}
        excludeSignalIds={inlineAnsweredIds}
      />

      {capturing && (
        <SignalCaptureModal
          workspaceId={workspaceId}
          porId={porId}
          cycleId={cycleId}
          planCycleId={planCycleId}
          onClose={() => setCapturing(false)}
          onRecorded={refresh}
        />
      )}
      {answering && (
        <AnswerSignalModal
          row={answering.row}
          initialKind={answering.kind}
          itemOptions={itemOptions}
          replanCycleId={replanCycleId ?? cycleId}
          onClose={() => setAnswering(null)}
          onAnswered={refresh}
        />
      )}
      {dismissing && (
        <DismissModal
          row={dismissing}
          onClose={() => setDismissing(null)}
          onDone={async () => {
            setDismissing(null);
            await refresh();
          }}
        />
      )}
      {recordingVerdict && (
        <VerdictModal
          row={recordingVerdict}
          onClose={() => setRecordingVerdict(null)}
          onDone={async () => {
            setRecordingVerdict(null);
            await refresh();
          }}
        />
      )}
      {reopening && (
        <ReopenModal
          row={reopening}
          onClose={() => setReopening(null)}
          onDone={async () => {
            setReopening(null);
            await refresh();
          }}
        />
      )}
    </section>
  );
}

function SignalRowItem({
  row,
  overdue,
  canAct,
  answered,
  onConfirm,
  onClassify,
  onAnswer,
  onDismiss,
  onRecordVerdict,
  onReopen,
}: {
  row: SignalEventRow;
  overdue: boolean;
  canAct: boolean;
  /** Latest change/hold answer linked to this signal — closes the row's loop. */
  answered?: SignalResponseRecordEntry;
  onConfirm: () => Promise<{ success: boolean; error?: string }>;
  onClassify: (rc: SignalRoutingClass, rationale?: string) => Promise<{ success: boolean; error?: string }>;
  onAnswer: (kind: 'hold' | 'change') => void;
  onDismiss: () => void;
  /** Break 4: close a signal out as held/broke/unobservable (canon 23 §6.5). */
  onRecordVerdict: () => void;
  /** Break 4: return a confirmed signal to monitoring. */
  onReopen: () => void;
}) {
  const [actionError, setActionError] = useState<string | null>(null);
  // Routing advisor (SIG-4): explicitly human-invoked, proposal-only.
  const [proposal, setProposal] = useState<SignalRoutingProposal | null>(null);
  const [proposalState, setProposalState] = useState<'idle' | 'loading' | 'none'>('idle');

  const fetchProposal = async () => {
    setProposalState('loading');
    const p = await proposeSignalRouting({
      signalEventId: row.id,
      occurrence: row.occurrence,
      message: row.message,
      severity: row.severity,
      workspaceId: row.workspace_id,
    });
    setProposal(p);
    setProposalState(p ? 'idle' : 'none');
  };

  return (
    <li className="px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
            {occurrenceLabel(row)}
            {overdue && (
              <span className="ml-2 text-[10px] font-mono uppercase tracking-wide text-luminous-coral-700">
                overdue
              </span>
            )}
          </p>
          {row.message && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5 line-clamp-2">{row.message}</p>
          )}
          <p className="text-[11px] font-mono text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
            {row.status === 'monitoring' ? 'watching' : 'confirmed'} · {row.source} · {age(row.created_at)}
            {row.routing_class && (
              <>
                {' · touches '}
                <span className="text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                  {ROUTING_SHORT[row.routing_class as SignalRoutingClass]}
                </span>
              </>
            )}
            {!answered && row.review_by && (
              <>
                {' · answer due '}
                <span className={overdue ? 'text-luminous-coral-700' : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]'}>
                  {new Date(row.review_by).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </>
            )}
          </p>
        </div>
        {canAct && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {!row.routing_class && proposalState !== 'loading' && !proposal && (
              <button
                onClick={() => void fetchProposal()}
                title="Ask the routing advisor for a suggestion (a human still decides)"
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]"
              >
                <Sparkles className="h-3.5 w-3.5" />
              </button>
            )}
            {!row.routing_class && (
              <select
                defaultValue=""
                onChange={async (e) => {
                  const rc = e.target.value as SignalRoutingClass | '';
                  if (!rc) return;
                  const r = await onClassify(rc);
                  if (!r.success) setActionError(r.error ?? 'Classify failed');
                }}
                className="text-xs border border-[var(--rule)] px-1.5 py-1 dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              >
                <option value="">Touches…</option>
                {SIGNAL_ROUTING_CLASSES.map((rc) => (
                  <option key={rc} value={rc}>
                    {ROUTING_SHORT[rc]}
                  </option>
                ))}
              </select>
            )}
            {row.status === 'monitoring' && (
              <button
                onClick={async () => {
                  const r = await onConfirm();
                  if (!r.success) setActionError(r.error ?? 'Confirm failed');
                }}
                className="text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                Confirm
              </button>
            )}
            {row.status === 'confirmed' && (
              <button
                onClick={onReopen}
                title="Return this signal to monitoring"
                className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
              >
                Reopen
              </button>
            )}
          </div>
        )}
      </div>

      {/* The loop closes here: a classified signal can be answered — hold or
          change, as a Decision on the record. Unclassified signals classify
          first (the RPC rejects them by name). An answered row shows its
          answer instead of buttons — the record, not a to-do. */}
      {answered ? (
        <div className="mt-2 border border-[var(--green)] bg-[var(--green-dim)]/40 px-3.5 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface-raised)]">
          <span className="block font-mono text-[9.5px] uppercase tracking-[0.14em] text-[var(--green)]/80 dark:text-[var(--text-ter)] mb-1">
            On the record
          </span>
          <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            <span className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {answered.responseKind === 'change' ? 'Changed course' : 'Held course'}
            </span>
            {answered.reconsiderationConditions && (
              <> — {extractExpectations(answered.reconsiderationConditions)[0]?.text ?? ''}</>
            )}
            {' — '}
            <Link
              to={`/prioritization/decisions/${answered.decisionId}`}
              className="text-primary-700 hover:underline dark:text-[var(--text-sec)]"
            >
              {answered.decisionTitle}
            </Link>
            {answered.reviewByDate &&
              `, check by ${formatCalendarDate(answered.reviewByDate, { month: 'short', day: 'numeric' })}`}
          </p>
          {/* Break 4: close the loop — did what this signal warned about hold
              or break? (canon 23 §6.5, mig 682). Shown here because this is
              where the answer that produced the signal's stakes already is. */}
          <div className="mt-2 pt-2 border-t border-[var(--green)]/40 dark:border-[var(--rule)]">
            {row.verdict ? (
              <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                <span className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  Verdict: {VERDICT_LABELS[row.verdict as SignalVerdict]}
                </span>
                {row.verdict_basis && <> — {row.verdict_basis}</>}
              </p>
            ) : (
              canAct && (
                <button
                  onClick={onRecordVerdict}
                  className="text-xs font-medium text-[var(--text-ter)] hover:underline"
                >
                  Record verdict
                  {row.review_by &&
                    ` (review by ${new Date(row.review_by).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`}
                </button>
              )
            )}
          </div>
        </div>
      ) : (
        canAct && (
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {row.routing_class && (
              <>
                <button
                  onClick={() => onAnswer('change')}
                  className="text-xs font-semibold px-3 py-1.5 bg-primary-700 text-white hover:bg-primary-800"
                >
                  Answer — change course
                </button>
                <button
                  onClick={() => onAnswer('hold')}
                  className="text-xs font-semibold px-3 py-1.5 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
                >
                  Hold course
                </button>
              </>
            )}
            <button
              onClick={onDismiss}
              className="text-xs font-semibold px-3 py-1.5 border border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--hover-tint)]"
            >
              Dismiss with reasons
            </button>
          </div>
        )
      )}
      {proposalState === 'loading' && (
        <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">Asking the routing advisor…</p>
      )}
      {proposalState === 'none' && (
        <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
          No suggestion available — classify it yourself with the selector.
        </p>
      )}
      {proposal && !row.routing_class && (
        <div className="flex items-start gap-2 mt-1.5 bg-[var(--surface)] dark:bg-[var(--surface-raised)] px-2.5 py-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Suggests <span className="font-medium">{ROUTING_SHORT[proposal.routingClass]}</span> — {proposal.reason}
          </p>
          <button
            onClick={async () => {
              const r = await onClassify(proposal.routingClass);
              if (!r.success) setActionError(r.error ?? 'Classify failed');
            }}
            className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] flex-shrink-0"
          >
            Use
          </button>
        </div>
      )}
      {actionError && <p className="text-xs text-luminous-coral-700 mt-1">{actionError}</p>}
    </li>
  );
}

function DismissModal({
  row,
  onClose,
  onDone,
}: {
  row: SignalEventRow;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDismiss = async () => {
    setBusy(true);
    const result = await dismissSignalEvent(row.id, rationale.trim());
    setBusy(false);
    if (!result.success) {
      setError(result.error ?? 'Could not dismiss the signal.');
      return;
    }
    await onDone();
  };

  return (
    <PorModal
      title="Dismiss signal"
      subtitle="Dismissal stays on the record with your rationale — no silent disposal."
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleDismiss} disabled={busy || rationale.trim().length === 0}>
            {busy ? 'Dismissing…' : 'Dismiss with rationale'}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-3">{occurrenceLabel(row)}</p>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
        Rationale <span className="text-luminous-coral-700">*</span>
      </label>
      <textarea
        value={rationale}
        onChange={(e) => {
          setRationale(e.target.value);
          if (error) setError(null);
        }}
        rows={3}
        placeholder="Why this signal does not warrant a response."
        className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none resize-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
      />
      {error && <p className="text-xs text-luminous-coral-700 mt-1.5">{error}</p>}
    </PorModal>
  );
}

// ── VerdictModal (break 4) ───────────────────────────────────────────────────
//
// record_signal_verdict (mig 682) is governance-gated (plan-governance
// authority), refuses a second verdict on the same signal, and refuses a
// dismissed signal. All three refusals come back as the RPC's own error
// text — shown verbatim, never paraphrased.

const VERDICT_OPTIONS: { value: SignalVerdict; label: string; hint: string }[] = [
  { value: 'held', label: 'Held', hint: 'What this signal warned about did not happen.' },
  { value: 'broke', label: 'Broke', hint: 'What this signal warned about happened.' },
  { value: 'unobservable', label: 'Unobservable', hint: 'There is no honest basis to say either way.' },
];

function VerdictModal({
  row,
  onClose,
  onDone,
}: {
  row: SignalEventRow;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [verdict, setVerdict] = useState<SignalVerdict>('held');
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRecord = async () => {
    setBusy(true);
    const result = await recordSignalVerdict(row.id, verdict, basis.trim());
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not record the verdict.');
      return;
    }
    await onDone();
  };

  return (
    <PorModal
      title="Record verdict"
      subtitle="Reflection-time judgment, recorded once — did what this signal warned about hold or break?"
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleRecord} disabled={busy || basis.trim().length === 0}>
            {busy ? 'Recording…' : 'Record verdict'}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-3">{occurrenceLabel(row)}</p>
      <div className="flex flex-col gap-1.5 mb-3">
        {VERDICT_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className={`flex items-start gap-2 border px-3 py-2 cursor-pointer ${
              verdict === opt.value
                ? 'border-primary-300 bg-primary-50 dark:bg-[var(--active-tint)]'
                : 'border-[var(--rule)] dark:border-[var(--rule)]'
            }`}
          >
            <input
              type="radio"
              name="verdict"
              checked={verdict === opt.value}
              onChange={() => setVerdict(opt.value)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                {opt.label}
              </span>
              <span className="block text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{opt.hint}</span>
            </span>
          </label>
        ))}
      </div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
        Basis <span className="text-luminous-coral-700">*</span>
      </label>
      <textarea
        value={basis}
        onChange={(e) => {
          setBasis(e.target.value);
          if (error) setError(null);
        }}
        rows={3}
        placeholder="What was observed."
        className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none resize-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
      />
      {error && <p className="text-xs text-luminous-coral-700 mt-1.5">{error}</p>}
    </PorModal>
  );
}

// ── ReopenModal (break 4) ────────────────────────────────────────────────────
//
// reopenSignalEvent (governed reversal, mig 736) returns a confirmed or
// dismissed signal to monitoring. Blocked once a response or a verdict has
// consumed it — the refusal names the consumer (blockedBy) and what to do
// instead (remedy), shown verbatim rather than folded into a generic error.

function ReopenModal({
  row,
  onClose,
  onDone,
}: {
  row: SignalEventRow;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedBy, setBlockedBy] = useState<string | null>(null);
  const [remedy, setRemedy] = useState<string | null>(null);

  const handleReopen = async () => {
    setBusy(true);
    const result = await reopenSignalEvent(row.id, reason.trim());
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not reopen the signal.');
      setBlockedBy(result.blockedBy ?? null);
      setRemedy(result.remedy ?? null);
      return;
    }
    await onDone();
  };

  return (
    <PorModal
      title="Reopen signal"
      subtitle="Returns this signal to monitoring. A written reason is required (canon 27 §6)."
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleReopen} disabled={busy || reason.trim().length === 0}>
            {busy ? 'Reopening…' : 'Reopen'}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-3">{occurrenceLabel(row)}</p>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
        Reason <span className="text-luminous-coral-700">*</span>
      </label>
      <textarea
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          if (error) { setError(null); setBlockedBy(null); setRemedy(null); }
        }}
        rows={3}
        placeholder="Why this should return to monitoring."
        className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none resize-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
      />
      {error && (
        <div className="mt-1.5">
          <p className="text-xs text-luminous-coral-700">{error}</p>
          {blockedBy && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              Blocked by: <span className="font-mono">{blockedBy}</span>
              {remedy && <> — {remedy}</>}
            </p>
          )}
        </div>
      )}
    </PorModal>
  );
}
